import type { EvidenceSource, ExtractedValue, VerificationStatus } from "@/lib/types";
import { readCase, writeCase } from "./store";
import { appendActivity } from "./activity";

export async function listEvidence(caseId: string): Promise<EvidenceSource[]> {
  return (await readCase(caseId))?.evidence ?? [];
}

/** Locates one extracted value and the source it came from. */
export async function findExtracted(
  caseId: string,
  valueId: string,
): Promise<{ source: EvidenceSource; value: ExtractedValue } | null> {
  for (const source of await listEvidence(caseId)) {
    const value = source.extracted.find((entry) => entry.id === valueId);
    if (value) return { source, value };
  }
  return null;
}

/** A recorded before/after, or null when the write was a no-op. */
interface Change {
  label: string;
  from: string;
  to: string;
}

async function mutateValue(
  caseId: string,
  valueId: string,
  apply: (value: ExtractedValue) => Change | null,
): Promise<Change | null> {
  let change: Change | null = null;
  await writeCase(caseId, (record) => {
    for (const source of record.evidence) {
      const value = source.extracted.find((entry) => entry.id === valueId);
      if (value) change = apply(value);
    }
  });
  return change;
}

export async function setVerification(
  caseId: string,
  valueId: string,
  status: VerificationStatus,
  actor: string,
): Promise<EvidenceSource[]> {
  const change = await mutateValue(caseId, valueId, (value) => {
    if (value.status === status) return null;
    const from = value.status;
    value.status = status;
    return { label: value.label, from, to: status };
  });

  if (change) {
    await appendActivity(caseId, {
      actor,
      kind: "evidence",
      title: `${change.label} marked ${change.to}`,
      from: change.from,
      to: change.to,
    });
  }

  return listEvidence(caseId);
}

/**
 * Corrects an extracted value. Confirming a correction verifies it — the
 * analyst has just read the source. The prior reading survives in the activity
 * trail, which is what an auditor will ask for.
 */
export async function editExtracted(
  caseId: string,
  valueId: string,
  newValue: string,
  actor: string,
): Promise<EvidenceSource[]> {
  const change = await mutateValue(caseId, valueId, (value) => {
    const trimmed = newValue.trim();
    if (trimmed === "" || trimmed === value.value) return null;
    const from = value.value;
    value.value = trimmed;
    value.status = "verified";
    value.confidence = 1;
    value.note = `Corrected from ${from} by ${actor}.`;
    return { label: value.label, from, to: trimmed };
  });

  if (change) {
    await appendActivity(caseId, {
      actor,
      kind: "evidence",
      title: `${change.label} corrected`,
      from: change.from,
      to: change.to,
    });
  }

  return listEvidence(caseId);
}
